import React, { useState, useEffect, useMemo } from "react";
import { SelectOption } from "../../input/SearchInput/types";
import { HgState } from "../../../types/taxonomy";
import { SearchInput } from "../../input/SearchInput/SearchInput";
import { useQueryStates } from "../../../contexts/Taxonomy/TaxonomyContext";
import { LOCATION_MESSAGES } from "./constants";

interface StateInputProps {
  countryAlpha2: string;
  selectedItems: string[];
  onStateSelect: (countryAlpha2: string, stateIds: string[]) => void;
}

const convertToStateOptions = (states: HgState[]): SelectOption[] =>
  states.map((state) => ({ label: state.name || "", value: state.id || "" }));

export const StateInput: React.FC<StateInputProps> = ({
  countryAlpha2,
  selectedItems = [],
  onStateSelect,
}) => {
  const [internalSelectedOptions, setInternalSelectedOptions] = useState<
    SelectOption[]
  >([]);
  const { data: states, loading } = useQueryStates({ countryAlpha2 });

  const isDisabled = useMemo(() => {
    return !countryAlpha2 || loading;
  }, [countryAlpha2, loading]);

  const options = useMemo(
    () => (states ? convertToStateOptions(states as HgState[]) : []),
    [states]
  );

  const selectedOptions = useMemo(() => {
    if (options.length === 0) return [];
    return options.filter((option) => selectedItems.includes(option.value));
  }, [options, selectedItems]);

  useEffect(() => {
    if (
      options.length > 0 &&
      selectedOptions.length > 0 &&
      JSON.stringify(internalSelectedOptions) !==
        JSON.stringify(selectedOptions)
    ) {
      setInternalSelectedOptions(selectedOptions);
    }
  }, [options, selectedOptions, internalSelectedOptions]);

  // Clear states when country changes
  useEffect(() => {
    setInternalSelectedOptions([]);
  }, [countryAlpha2]);

  const handleOptionsSelect = (options: SelectOption[]) => {
    if (JSON.stringify(options) !== JSON.stringify(internalSelectedOptions)) {
      setInternalSelectedOptions(options);
      onStateSelect(
        countryAlpha2,
        options.map((option) => option.value)
      );
    }
  };

  const placeholder = useMemo(() => {
    if (!countryAlpha2) return "Select a country first";
    if (loading) return "Loading...";
    if (options.length === 0) return LOCATION_MESSAGES.STATE.NO_RESULTS;

    return LOCATION_MESSAGES.STATE.PLACEHOLDER;
  }, [loading, countryAlpha2, options]);

  return (
    <SearchInput
      id="states"
      disabled={isDisabled || options.length === 0}
      options={options}
      selectedOptions={internalSelectedOptions}
      onOptionsSelect={handleOptionsSelect}
      placeholderText={placeholder}
      noResultsMessage={LOCATION_MESSAGES.STATE.NO_RESULTS}
      minFilterLength={0}
    />
  );
};
